import { useCallback, useLayoutEffect, useRef, useState } from "react";

import {
  DEFAULT_CENTER,
  DEFAULT_ZOOM,
  SEARCH_DEBOUNCE,
  VIEWPORT_DEBOUNCE,
} from "../config";
import { fetchHotelMapPois, searchHotelMapPlaces } from "../repo";
import { hotelMapErrorMessage, isLodgingPoi } from "../service";
import type {
  HotelMapCoordinate,
  HotelMapEventHandler,
  HotelMapFactory,
  HotelMapInstance,
  HotelMapPoi,
  HotelMapSearchItem,
  HotelMapSearchResult,
  HotelMapViewport,
} from "../types";
import { amapHotelMapFactory } from "./amapAdapter";

/** 酒店地图 runtime 可替换依赖。 */
export type HotelMapRuntimeOptions = {
  mapFactory?: HotelMapFactory;
  fetchPois?: (viewport: HotelMapViewport, signal?: AbortSignal) => Promise<HotelMapPoi[]>;
  searchPlaces?: (query: string) => Promise<HotelMapSearchResult>;
};

/** 酒店地图 runtime 对页面暴露的稳定状态与操作。 */
export type HotelMapRuntime = {
  mapReady: boolean;
  loadingPois: boolean;
  locatingCurrentPosition: boolean;
  searching: boolean;
  notice: string | null;
  error: string | null;
  areas: HotelMapSearchItem[];
  points: HotelMapSearchItem[];
  searchQuery: string;
  mount: (container: HTMLElement) => Promise<void>;
  setSearchQuery: (value: string) => void;
  selectSearchItem: (item: HotelMapSearchItem) => void;
  goToCurrentPosition: () => Promise<void>;
  retry: () => Promise<void>;
};

type RuntimeDependencies = Required<HotelMapRuntimeOptions>;

type ErrorSource = "map" | "location" | "pois" | "search";

type CameraIntentContext = {
  aliveRef: { current: boolean };
  lifecycleRef: { current: number };
  cameraIntentSequenceRef: { current: number };
  mapRef: { current: HotelMapInstance | null };
};

/** 管理酒店地图定位、视口 POI、地点搜索与异步竞态。 */
export function useHotelMapRuntime(
  options: HotelMapRuntimeOptions = {},
): HotelMapRuntime {
  const dependenciesRef = useRef<RuntimeDependencies>(resolveDependencies(options));
  dependenciesRef.current = resolveDependencies(options);

  const [mapReady, setMapReady] = useState(false);
  const [loadingPois, setLoadingPois] = useState(false);
  const [locatingCurrentPosition, setLocatingCurrentPosition] = useState(false);
  const [searching, setSearching] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [areas, setAreas] = useState<HotelMapSearchItem[]>([]);
  const [points, setPoints] = useState<HotelMapSearchItem[]>([]);
  const [searchQuery, setSearchQueryState] = useState("");

  const aliveRef = useRef(true);
  const lifecycleRef = useRef(0);
  const mapRef = useRef<HotelMapInstance | null>(null);
  const lastContainerRef = useRef<HTMLElement | null>(null);
  const mapListenerRef = useRef<{
    map: HotelMapInstance;
    handler: HotelMapEventHandler;
  } | null>(null);
  const viewportTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const poiAbortControllerRef = useRef<AbortController | null>(null);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const viewportSequenceRef = useRef(0);
  const cameraIntentSequenceRef = useRef(0);
  const cameraIntentWaitersRef = useRef(new Set<() => void>());
  const searchSequenceRef = useRef(0);
  const searchQueryRef = useRef("");
  const initialSearchScheduledRef = useRef(false);
  const mountingLifecycleRef = useRef<number | null>(null);
  const pendingSearchTargetRef = useRef<{
    lifecycle: number;
    coordinate: HotelMapCoordinate;
    showTarget: boolean;
  } | null>(null);
  const errorsRef = useRef<Record<ErrorSource, string | null>>({
    map: null,
    location: null,
    pois: null,
    search: null,
  });

  const advanceCameraIntent = useCallback((): number => {
    cameraIntentSequenceRef.current += 1;
    const waiters = [...cameraIntentWaitersRef.current];
    cameraIntentWaitersRef.current.clear();
    waiters.forEach((wake) => wake());
    return cameraIntentSequenceRef.current;
  }, []);

  const waitForCameraIntentChange = useCallback((sequence: number) => {
    let waiter: (() => void) | null = null;
    const promise = new Promise<void>((resolve) => {
      if (sequence !== cameraIntentSequenceRef.current) {
        resolve();
        return;
      }
      waiter = resolve;
      cameraIntentWaitersRef.current.add(waiter);
    });
    return {
      promise,
      cancel: () => {
        if (waiter) {
          cameraIntentWaitersRef.current.delete(waiter);
        }
      },
    };
  }, []);

  const publishError = useCallback((source: ErrorSource, message: string | null) => {
    errorsRef.current[source] = message;
    if (!aliveRef.current) {
      return;
    }
    const currentSource = selectErrorSource(errorsRef.current);
    setError(currentSource ? errorsRef.current[currentSource] : null);
  }, []);

  const disposeMap = useCallback((updateState: boolean) => {
    lifecycleRef.current += 1;
    viewportSequenceRef.current += 1;
    advanceCameraIntent();
    mountingLifecycleRef.current = null;
    pendingSearchTargetRef.current = null;
    clearTimer(viewportTimerRef);
    poiAbortControllerRef.current?.abort();
    poiAbortControllerRef.current = null;

    const listener = mapListenerRef.current;
    mapListenerRef.current = null;
    if (listener) {
      safely(() => listener.map.off("moveend", listener.handler));
      safely(() => listener.map.off("zoomend", listener.handler));
    }
    const map = mapRef.current;
    mapRef.current = null;
    if (map) {
      safely(() => map.destroy());
    }

    if (updateState && aliveRef.current) {
      setMapReady(false);
      setLoadingPois(false);
      setLocatingCurrentPosition(false);
    }
  }, [advanceCameraIntent]);

  const requestPois = useCallback(async (
    map: HotelMapInstance,
    lifecycle: number,
    sequence: number,
  ): Promise<void> => {
    if (!isCurrentMapRequest(
      aliveRef,
      lifecycleRef,
      viewportSequenceRef,
      mapRef,
      map,
      lifecycle,
      sequence,
    )) {
      return;
    }
    poiAbortControllerRef.current?.abort();
    const controller = new AbortController();
    poiAbortControllerRef.current = controller;
    setLoadingPois(true);
    try {
      const items = await dependenciesRef.current.fetchPois(map.getViewport(), controller.signal);
      if (!isCurrentMapRequest(
        aliveRef,
        lifecycleRef,
        viewportSequenceRef,
        mapRef,
        map,
        lifecycle,
        sequence,
      )) {
        return;
      }
      map.replaceHotelMarkers(items.filter(isLodgingPoi));
      publishError("pois", null);
    } catch (requestError) {
      if (controller.signal.aborted || isAbortError(requestError)) {
        return;
      }
      if (isCurrentMapRequest(
        aliveRef,
        lifecycleRef,
        viewportSequenceRef,
        mapRef,
        map,
        lifecycle,
        sequence,
      )) {
        publishError("pois", hotelMapErrorMessage(requestError));
      }
    } finally {
      if (poiAbortControllerRef.current === controller) {
        poiAbortControllerRef.current = null;
      }
      if (isCurrentMapRequest(
        aliveRef,
        lifecycleRef,
        viewportSequenceRef,
        mapRef,
        map,
        lifecycle,
        sequence,
      )) {
        setLoadingPois(false);
      }
    }
  }, [publishError]);

  const requestSearch = useCallback(async (
    query: string,
    sequence: number,
  ): Promise<void> => {
    try {
      const result = await dependenciesRef.current.searchPlaces(query);
      if (!aliveRef.current || sequence !== searchSequenceRef.current) {
        return;
      }
      setAreas(result.areas);
      setPoints(result.points);
      publishError("search", null);
    } catch {
      if (aliveRef.current && sequence === searchSequenceRef.current) {
        publishError("search", "地点搜索失败，请重试");
      }
    } finally {
      if (aliveRef.current && sequence === searchSequenceRef.current) {
        setSearching(false);
      }
    }
  }, [publishError]);

  const scheduleSearch = useCallback((query: string) => {
    if (!aliveRef.current) {
      return;
    }
    clearTimer(searchTimerRef);
    const sequence = searchSequenceRef.current + 1;
    searchSequenceRef.current = sequence;
    setSearching(true);
    publishError("search", null);
    searchTimerRef.current = globalThis.setTimeout(() => {
      searchTimerRef.current = null;
      if (!aliveRef.current || sequence !== searchSequenceRef.current) {
        return;
      }
      void requestSearch(query, sequence);
    }, SEARCH_DEBOUNCE);
  }, [publishError, requestSearch]);

  const applySearchTarget = useCallback((
    map: HotelMapInstance,
    coordinate: HotelMapCoordinate,
    showTarget = true,
  ): boolean => {
    try {
      if (showTarget) {
        map.setSearchTarget(coordinate);
      } else {
        map.clearSearchTarget();
      }
      map.setCenterAndZoom(coordinate, DEFAULT_ZOOM);
      setNotice(null);
      publishError("location", null);
      return true;
    } catch {
      disposeMap(true);
      publishError("map", "地图初始化失败，请重试");
      return false;
    }
  }, [disposeMap, publishError]);

  const mount = useCallback(async (container: HTMLElement): Promise<void> => {
    if (!aliveRef.current) {
      return;
    }
    lastContainerRef.current = container;
    disposeMap(true);
    const lifecycle = lifecycleRef.current;
    const initialCameraIntentSequence = cameraIntentSequenceRef.current;
    mountingLifecycleRef.current = lifecycle;
    errorsRef.current.map = null;
    errorsRef.current.location = null;
    errorsRef.current.pois = null;
    setError(errorsRef.current.search);
    setNotice(null);

    let map: HotelMapInstance;
    try {
      map = await dependenciesRef.current.mapFactory.create(container, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
      });
    } catch {
      if (aliveRef.current && lifecycle === lifecycleRef.current) {
        mountingLifecycleRef.current = null;
        pendingSearchTargetRef.current = null;
        publishError("map", "地图初始化失败，请重试");
      }
      return;
    }

    if (!aliveRef.current || lifecycle !== lifecycleRef.current) {
      safely(() => map.destroy());
      return;
    }

    const handleViewportChange = () => {
      if (!isCurrentMapLifecycle(aliveRef, lifecycleRef, mapRef, map, lifecycle)) {
        return;
      }
      clearTimer(viewportTimerRef);
      const sequence = viewportSequenceRef.current + 1;
      viewportSequenceRef.current = sequence;
      viewportTimerRef.current = globalThis.setTimeout(() => {
        if (!isCurrentMapRequest(
          aliveRef,
          lifecycleRef,
          viewportSequenceRef,
          mapRef,
          map,
          lifecycle,
          sequence,
        )) {
          return;
        }
        viewportTimerRef.current = null;
        void requestPois(map, lifecycle, sequence);
      }, VIEWPORT_DEBOUNCE);
    };
    mapRef.current = map;
    mapListenerRef.current = { map, handler: handleViewportChange };
    try {
      map.on("moveend", handleViewportChange);
      map.on("zoomend", handleViewportChange);
    } catch {
      disposeMap(true);
      publishError("map", "地图初始化失败，请重试");
      return;
    }

    if (mountingLifecycleRef.current === lifecycle) {
      mountingLifecycleRef.current = null;
    }
    const pendingSearchTarget = pendingSearchTargetRef.current;
    if (pendingSearchTarget?.lifecycle === lifecycle) {
      pendingSearchTargetRef.current = null;
      if (!applySearchTarget(
        map,
        pendingSearchTarget.coordinate,
        pendingSearchTarget.showTarget,
      )) {
        return;
      }
    } else if (initialCameraIntentSequence === cameraIntentSequenceRef.current) {
      const cameraIntentChange = waitForCameraIntentChange(initialCameraIntentSequence);
      try {
        await Promise.race([
          locateMap(map, lifecycle, initialCameraIntentSequence, {
            aliveRef,
            lifecycleRef,
            cameraIntentSequenceRef,
            mapRef,
            setNotice,
          }),
          cameraIntentChange.promise,
        ]);
      } catch {
        if (isCurrentMapLifecycle(aliveRef, lifecycleRef, mapRef, map, lifecycle)) {
          disposeMap(true);
          publishError("map", "地图初始化失败，请重试");
        }
        return;
      } finally {
        cameraIntentChange.cancel();
      }
    }
    if (!isCurrentMapLifecycle(aliveRef, lifecycleRef, mapRef, map, lifecycle)) {
      return;
    }
    setMapReady(true);
    publishError("map", null);
    handleViewportChange();
  }, [applySearchTarget, disposeMap, publishError, requestPois, waitForCameraIntentChange]);

  const setSearchQuery = useCallback((value: string) => {
    if (!aliveRef.current) {
      return;
    }
    searchQueryRef.current = value;
    setSearchQueryState(value);
    scheduleSearch(value);
  }, [scheduleSearch]);

  const selectSearchItem = useCallback((item: HotelMapSearchItem) => {
    if (!aliveRef.current) {
      return;
    }
    const coordinate = { lng: item.lng, lat: item.lat };
    const showTarget = item.type !== "hotel";
    const map = mapRef.current;
    if (!map) {
      const lifecycle = mountingLifecycleRef.current;
      if (lifecycle === null || lifecycle !== lifecycleRef.current) {
        return;
      }
      advanceCameraIntent();
      pendingSearchTargetRef.current = { lifecycle, coordinate, showTarget };
      setLocatingCurrentPosition(false);
      return;
    }
    advanceCameraIntent();
    setLocatingCurrentPosition(false);
    applySearchTarget(map, coordinate, showTarget);
  }, [advanceCameraIntent, applySearchTarget]);

  const goToCurrentPosition = useCallback(async (): Promise<void> => {
    const map = mapRef.current;
    if (!aliveRef.current || !map) {
      return;
    }
    const lifecycle = lifecycleRef.current;
    const sequence = advanceCameraIntent();
    const cameraIntentContext = {
      aliveRef,
      lifecycleRef,
      cameraIntentSequenceRef,
      mapRef,
    };
    setLocatingCurrentPosition(true);
    publishError("location", null);

    try {
      const coordinate = await map.locateCurrentPosition();
      if (!isCurrentCameraIntent(cameraIntentContext, map, lifecycle, sequence)) {
        return;
      }
      if (!isValidCoordinate(coordinate)) {
        publishError("location", "无法获取当前位置，请检查定位权限");
        return;
      }
      try {
        map.setCenterAndZoom(coordinate, DEFAULT_ZOOM);
        map.clearSearchTarget();
      } catch {
        disposeMap(true);
        publishError("map", "地图初始化失败，请重试");
        return;
      }
      setNotice(null);
      publishError("location", null);
    } catch {
      if (isCurrentCameraIntent(cameraIntentContext, map, lifecycle, sequence)) {
        publishError("location", "无法获取当前位置，请检查定位权限");
      }
    } finally {
      if (isCurrentCameraIntent(cameraIntentContext, map, lifecycle, sequence)) {
        setLocatingCurrentPosition(false);
      }
    }
  }, [advanceCameraIntent, disposeMap, publishError]);

  const retry = useCallback(async (): Promise<void> => {
    if (!aliveRef.current) {
      return;
    }
    const source = selectErrorSource(errorsRef.current);
    if (source === "map") {
      const container = lastContainerRef.current;
      if (container) {
        await mount(container);
      }
      return;
    }
    if (source === "location") {
      await goToCurrentPosition();
      return;
    }
    if (source === "search") {
      clearTimer(searchTimerRef);
      const sequence = searchSequenceRef.current + 1;
      searchSequenceRef.current = sequence;
      setSearching(true);
      await requestSearch(searchQueryRef.current, sequence);
      return;
    }
    const map = mapRef.current;
    if (!map) {
      const container = lastContainerRef.current;
      if (container) {
        await mount(container);
      }
      return;
    }
    clearTimer(viewportTimerRef);
    const sequence = viewportSequenceRef.current + 1;
    viewportSequenceRef.current = sequence;
    await requestPois(map, lifecycleRef.current, sequence);
  }, [goToCurrentPosition, mount, requestPois, requestSearch]);

  useLayoutEffect(() => {
    aliveRef.current = true;
    if (!initialSearchScheduledRef.current) {
      initialSearchScheduledRef.current = true;
      scheduleSearch("");
    }
    return () => {
      aliveRef.current = false;
      initialSearchScheduledRef.current = false;
      disposeMap(false);
      searchSequenceRef.current += 1;
      clearTimer(searchTimerRef);
    };
  }, [disposeMap, scheduleSearch]);

  return {
    mapReady,
    loadingPois,
    locatingCurrentPosition,
    searching,
    notice,
    error,
    areas,
    points,
    searchQuery,
    mount,
    setSearchQuery,
    selectSearchItem,
    goToCurrentPosition,
    retry,
  };
}

/** 解析生产默认依赖，并允许测试替换浏览器边界。 */
function resolveDependencies(options: HotelMapRuntimeOptions): RuntimeDependencies {
  return {
    mapFactory: options.mapFactory ?? amapHotelMapFactory,
    fetchPois: options.fetchPois ?? fetchHotelMapPois,
    searchPlaces: options.searchPlaces ?? searchHotelMapPlaces,
  };
}

/** 完成精确定位及城市、北京两级降级。 */
async function locateMap(
  map: HotelMapInstance,
  lifecycle: number,
  sequence: number,
  context: CameraIntentContext & {
    setNotice: (notice: string | null) => void;
  },
): Promise<void> {
  if (!isCurrentCameraIntent(context, map, lifecycle, sequence)) {
    return;
  }
  let precise: HotelMapCoordinate | null = null;
  try {
    precise = await map.locateCurrentPosition();
  } catch {
    // 精确定位失败时继续使用城市定位，不阻断地图加载。
  }
  if (!isCurrentCameraIntent(context, map, lifecycle, sequence)) {
    return;
  }
  if (precise && isValidCoordinate(precise)) {
    map.setCenterAndZoom(precise, DEFAULT_ZOOM);
    context.setNotice(null);
    return;
  }

  let cityCenter: HotelMapCoordinate | null = null;
  try {
    cityCenter = await map.locateCityCenter();
  } catch {
    // 城市定位失败继续使用北京默认中心，不阻断地图。
  }
  if (!isCurrentCameraIntent(context, map, lifecycle, sequence)) {
    return;
  }
  if (cityCenter && isValidCoordinate(cityCenter)) {
    map.setCenterAndZoom(cityCenter, DEFAULT_ZOOM);
    context.setNotice("未获得精确位置，已定位到当前城市");
    return;
  }
  map.setCenterAndZoom(DEFAULT_CENTER, DEFAULT_ZOOM);
  context.setNotice("未获得精确位置和城市位置，已显示北京");
}

/** 判断坐标是有限且处于经纬度法定范围内。 */
function isValidCoordinate(coordinate: HotelMapCoordinate): boolean {
  return Number.isFinite(coordinate.lng)
    && Number.isFinite(coordinate.lat)
    && coordinate.lng >= -180
    && coordinate.lng <= 180
    && coordinate.lat >= -90
    && coordinate.lat <= 90;
}

/** 判断异常是否来自已取消的浏览器或 Axios 请求。 */
function isAbortError(error: unknown): boolean {
  return error instanceof DOMException
    ? error.name === "AbortError"
    : typeof error === "object"
      && error !== null
      && ("code" in error && error.code === "ERR_CANCELED"
        || "name" in error && error.name === "CanceledError");
}

/** 判断地图请求仍属于当前 hook、地图和最新视口意图。 */
function isCurrentMapRequest(
  aliveRef: { current: boolean },
  lifecycleRef: { current: number },
  sequenceRef: { current: number },
  mapRef: { current: HotelMapInstance | null },
  map: HotelMapInstance,
  lifecycle: number,
  sequence: number,
): boolean {
  return isCurrentMapLifecycle(aliveRef, lifecycleRef, mapRef, map, lifecycle)
    && sequence === sequenceRef.current;
}

/** 判断定位结果仍属于当前地图与最新相机意图。 */
function isCurrentCameraIntent(
  context: CameraIntentContext,
  map: HotelMapInstance,
  lifecycle: number,
  sequence: number,
): boolean {
  return isCurrentMapRequest(
    context.aliveRef,
    context.lifecycleRef,
    context.cameraIntentSequenceRef,
    context.mapRef,
    map,
    lifecycle,
    sequence,
  );
}

/** 判断事件与定时器仍属于当前地图实例及生命周期。 */
function isCurrentMapLifecycle(
  aliveRef: { current: boolean },
  lifecycleRef: { current: number },
  mapRef: { current: HotelMapInstance | null },
  map: HotelMapInstance,
  lifecycle: number,
): boolean {
  return aliveRef.current
    && lifecycle === lifecycleRef.current
    && mapRef.current === map;
}

/** 按公开错误展示顺序选择 retry 的唯一目标。 */
function selectErrorSource(errors: Record<ErrorSource, string | null>): ErrorSource | null {
  if (errors.map) {
    return "map";
  }
  if (errors.location) {
    return "location";
  }
  if (errors.pois) {
    return "pois";
  }
  return errors.search ? "search" : null;
}

/** 清理并复位单个定时器引用。 */
function clearTimer(ref: { current: ReturnType<typeof setTimeout> | null }): void {
  if (ref.current !== null) {
    globalThis.clearTimeout(ref.current);
    ref.current = null;
  }
}

/** 清理第三方地图资源时吞掉异常，确保后续资源继续释放。 */
function safely(action: () => void): void {
  try {
    action();
  } catch {
    // 第三方清理异常不应中断其余 runtime 回收。
  }
}
